import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shirt, Layers, CupSoda, ShoppingBag, Frame, Printer, Footprints, PartyPopper, ArrowRight,
} from "lucide-react";

const ACCENT = {
  cyan: { text: "text-brand-cyan", border: "hover:border-brand-cyan/60", bar: "bg-brand-cyan" },
  magenta: { text: "text-brand-magenta", border: "hover:border-brand-magenta/60", bar: "bg-brand-magenta" },
  yellow: { text: "text-brand-yellow", border: "hover:border-brand-yellow/60", bar: "bg-brand-yellow" },
};

const APPAREL_LINKS = [
  { to: "/product/dtf-custom-t-shirt", label: "DTF Custom T-Shirt" },
  { to: "/product/sublimation-custom-t-shirt", label: "Sublimation Custom T-Shirt" },
  { to: "/product/htv-vinyl-custom-t-shirt", label: "HTV / Vinyl Custom T-Shirt" },
  { to: "/product/birthday-memorial-shirt", label: "Birthday & Memorial Shirt" },
  { to: "/product/custom-jersey", label: "Jerseys & Team Wear" },
];

const CARDS = [
  { title: "Custom Apparel", icon: Shirt, accent: "cyan", desc: "T-shirts, tribute shirts and team wear — your garment or ours.", links: APPAREL_LINKS },
  { title: "Stickers & Labels", icon: Layers, accent: "magenta", desc: "Sheet-fed stickers in economy paper, white or clear vinyl.", links: [{ to: "/product/custom-stickers-labels", label: "Open the Sticker Builder" }] },
  { title: "Drinkware", icon: CupSoda, accent: "magenta", desc: "Mugs, tumblers, snow globes and kids cups.", links: [{ to: "/shop/custom-drinkware", label: "Browse Drinkware" }] },
  { title: "Bags & Accessories", icon: ShoppingBag, accent: "yellow", desc: "Totes, hats, keychains, pouches and car gear.", links: [{ to: "/shop/custom-bags-accessories", label: "Browse Bags & Accessories" }] },
  { title: "Personalized Gifts", icon: Frame, accent: "cyan", desc: "Photo frames, slates, buttons, magnets and keepsakes.", links: [{ to: "/shop/photo-gift-items", label: "Browse Personalized Gifts" }] },
  { title: "Business & Branding", icon: Printer, accent: "magenta", desc: "Business cards, flyers, merch packs and launch bundles.", links: [{ to: "/shop/business-printing", label: "Browse Business Printing" }] },
  { title: "Custom Footwear", icon: Footprints, accent: "cyan", desc: "One-of-one sneaker customs and refresh & repaint.", links: [{ to: "/shop/customized-sneakers", label: "Browse Custom Footwear" }] },
  { title: "Events & Occasions", icon: PartyPopper, accent: "yellow", desc: "Birthday and graduation packages plus party add-ons.", links: [{ to: "/shop/events-occasions", label: "Browse Events & Occasions" }] },
];

const BuilderLanding = () => (
  <div data-testid="builder-landing" className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6">
    <header className="mb-12 max-w-3xl">
      <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-cyan">Print Builder</p>
      <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
        What Are We Making Today?
      </h1>
      <p className="mt-4 text-base leading-relaxed text-zinc-400">
        Pick a product line to open its interactive builder — live pricing, artwork upload and
        instant quotes on every configuration.
      </p>
    </header>

    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {CARDS.map((c, i) => {
        const ac = ACCENT[c.accent];
        const Icon = c.icon;
        return (
          <motion.div
            key={c.title}
            data-testid={`builder-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className={`flex flex-col border border-white/10 bg-ink-2 p-5 transition-colors ${ac.border}`}
          >
            <div className={`mb-4 h-0.5 w-10 ${ac.bar}`} />
            <Icon className={`h-7 w-7 ${ac.text}`} strokeWidth={1.5} />
            <h2 className="mt-3 font-display text-lg font-black uppercase tracking-wide text-white">
              {c.title}
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{c.desc}</p>
            <div className="mt-4 space-y-1.5 border-t border-white/10 pt-3">
              {c.links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`builder-card-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`group flex items-center justify-between gap-2 font-mono text-[11px] font-bold uppercase tracking-wider ${ac.text}`}
                >
                  {l.label}
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

export default BuilderLanding;
